import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Store, ArrowLeft, ShoppingBag, Package, Sparkles, CheckCircle2 } from 'lucide-react';

import { Navbar } from '../../../components/Navbar.jsx';
import { RegistroClienteForm } from './RegistroClienteForm.jsx';
import { RegistroComercianteForm } from './RegistroComercianteForm.jsx';

// --- FONDO AMBIENTAL (Compartido) ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#020202]">
    <div className="absolute top-[-10%] left-[-10%] w-[60vw] h-[60vw] bg-indigo-900/20 rounded-full blur-[150px] opacity-30 mix-blend-screen animate-pulse" />
    <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-purple-900/10 rounded-full blur-[150px] opacity-20 mix-blend-screen" />
  </div>
);

// --- PÁGINA PRINCIPAL (SELECCIÓN) ---
export function RegistroPage() {
  const [tipoUsuario, setTipoUsuario] = useState(null); // 'cliente' | 'comerciante' | null

  // --- FIX SCROLL: Resetear posición al cambiar de pantalla ---
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tipoUsuario]);

  if (tipoUsuario === 'cliente') {
    return <RegistroClientePage onVolver={() => setTipoUsuario(null)} />;
  }

  if (tipoUsuario === 'comerciante') {
    return <RegistroComerciantePage onVolver={() => setTipoUsuario(null)} />;
  }

  return (
    <div className="min-h-screen text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col">
      <AmbientBackground />
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-5xl relative z-10">
          
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3">
              Únete a <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">TiendasYA</span>
            </h1>
            <p className="text-white/50 text-sm max-w-xl mx-auto">
              El ecosistema digital donde compradores y emprendedores conectan sin límites.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            
            {/* TARJETA CLIENTE */}
            <div
              onClick={() => setTipoUsuario('cliente')}
              className="group relative rounded-[1.5rem] border border-white/10 bg-[#0F0F0F]/60 backdrop-blur-xl p-6 cursor-pointer transition-all hover:border-cyan-500/30 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(6,182,212,0.15)] overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <User className="h-6 w-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Soy Cliente</h3>
                <p className="text-white/50 text-xs mb-5 leading-relaxed">
                   Explora miles de productos, gestiona tus compras y descubre ofertas exclusivas en un solo lugar.
                </p>
                <ul className="space-y-2 text-left w-full max-w-[180px] mx-auto">
                    {['Compras Seguras', 'Tracking en Vivo', 'Ofertas Flash'].map(item => (
                        <li key={item} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-cyan-200/70">
                            <CheckCircle2 className="h-3 w-3 text-cyan-500 flex-shrink-0" /> {item}
                        </li>
                    ))}
                </ul>
              </div>
            </div>

            {/* TARJETA COMERCIANTE */}
            <div
              onClick={() => setTipoUsuario('comerciante')}
              className="group relative rounded-[1.5rem] border border-white/10 bg-[#0F0F0F]/60 backdrop-blur-xl p-6 cursor-pointer transition-all hover:border-emerald-500/30 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(16,185,129,0.15)] overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Store className="h-6 w-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Soy Comerciante</h3>
                <p className="text-white/50 text-xs mb-5 leading-relaxed">
                   Crea tu tienda online profesional en minutos. Gestiona productos, pedidos y clientes desde un solo lugar.
                </p>
                <ul className="space-y-2 text-left w-full max-w-[180px] mx-auto">
                    {['Configuración Rápida', 'Gestión Completa', 'Panel Avanzado'].map(item => (
                        <li key={item} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-200/70">
                            <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" /> {item}
                        </li>
                    ))}
                </ul>
              </div>
            </div>

          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-white/40">
              ¿Ya tienes una cuenta?{' '}
              <Link to="/inicio-sesion" className="font-bold text-indigo-400 hover:text-white transition-colors border-b border-indigo-500/30 pb-0.5">
                Iniciar Sesión
              </Link>
            </p>
          </div>

        </div>
      </main>
    </div>
  );
}

// --- PÁGINA REGISTRO CLIENTE ---
function RegistroClientePage({ onVolver }) {
  // FIX: Cambiado h-screen por min-h-screen y reducido padding para evitar scroll
  return (
    <div className="min-h-screen w-full text-slate-200 font-sans selection:bg-cyan-500/30 flex flex-col">
      <AmbientBackground />
      <div className="flex-shrink-0 z-50">
        <Navbar />
      </div>
      
      <main className="flex-1 flex items-center justify-center px-4 py-6 relative">
        <div className="w-full max-w-4xl relative z-10">
          
          <button
            onClick={onVolver}
            className="mb-3 group inline-flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" /> Volver
          </button>

          <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" />

            <div className="grid lg:grid-cols-5 gap-0">
                
                {/* Columna Izquierda (Branding) - Más compacta */}
                <div className="hidden lg:flex lg:col-span-2 bg-white/[0.02] border-r border-white/5 p-6 flex-col justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 to-transparent opacity-30" />
                    <div className="relative z-10">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
                            <ShoppingBag className="w-5 h-5 text-cyan-400" />
                        </div>
                        <h1 className="text-xl font-bold text-white tracking-tight leading-tight mb-2">
                            Tu Mundo, <br/> A un Click.
                        </h1>
                        <p className="text-white/50 text-[11px] leading-relaxed mb-4">
                            Crea tu cuenta personal para guardar favoritos, seguir pedidos y comprar más rápido.
                        </p>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-cyan-400/80">
                                <Sparkles className="w-3 h-3" /> Acceso Inmediato
                            </div>
                            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-cyan-400/80">
                                <Sparkles className="w-3 h-3" /> 100% Gratuito
                            </div>
                        </div>
                    </div>
                </div>

                {/* Columna Derecha (Formulario) - Más compacta */}
                <div className="col-span-5 lg:col-span-3 p-5 md:p-6 flex flex-col justify-center">
                   <div className="lg:hidden mb-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-cyan-400" />
                        <h2 className="text-base font-bold text-white">Crear Cuenta Cliente</h2>
                   </div>
                   
                   <RegistroClienteForm />
                
                </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// --- PÁGINA REGISTRO COMERCIANTE (Reutilizando diseño luxury) ---
function RegistroComerciantePage({ onVolver }) {
  // FIX: Reducido padding vertical para que quepa sin scroll
  return (
    <div className="min-h-screen w-full text-slate-200 font-sans selection:bg-emerald-500/30 flex flex-col">
      <AmbientBackground />
      <div className="flex-shrink-0 z-50">
        <Navbar />
      </div>
      
      <main className="flex-1 flex items-center justify-center px-4 py-6 relative">
        <div className="w-full max-w-4xl relative z-10">
          
          <button
            onClick={onVolver}
            className="mb-3 group inline-flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" /> Volver
          </button>

          <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent opacity-50" />

            <div className="grid lg:grid-cols-5 gap-0">
                <div className="hidden lg:flex lg:col-span-2 bg-white/[0.02] border-r border-white/5 p-6 flex-col justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 to-transparent opacity-30" />
                    <div className="relative z-10">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                            <Store className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h1 className="text-xl font-bold text-white tracking-tight leading-tight mb-2">
                            Tu Negocio, <br/> Siguiente Nivel.
                        </h1>
                        <p className="text-white/50 text-[11px] leading-relaxed mb-4">
                            Plataforma todo en uno para comercio digital. Configuración inmediata.
                        </p>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-emerald-400/80">
                                <Sparkles className="w-3 h-3" /> Prueba gratuita 15 días
                            </div>
                            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-emerald-400/80">
                                <Sparkles className="w-3 h-3" /> Sin tarjeta de crédito
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-span-5 lg:col-span-3 p-5 md:p-6 flex flex-col justify-center">
                   <div className="lg:hidden mb-3 flex items-center gap-2">
                        <Store className="w-4 h-4 text-emerald-400" />
                        <h2 className="text-base font-bold text-white">Crear Tienda</h2>
                   </div>
                   <RegistroComercianteForm />
                </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}